// Global Instructions Rule Applied!
import React, { useRef, useState, useEffect } from 'react'
import { Table, Button as AntButton, Popconfirm, Dropdown } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TableProps } from 'antd/es/table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faPlus,
  faTrash,
  faCheck,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons'
import { FormSection } from '../FormSection.tsx'
import { TIMING, BUTTON_CLASSES, TEXT_CLASSES, UI, DEFAULT_PROPS } from './constants.ts'
import Button from '../Button.jsx'

// Record type - must have a key property
export interface FormTableRecord {
  key: string | number
  [key: string]: any
}

// Scroll state interface
interface ScrollState {
  canScrollLeft: boolean
  canScrollRight: boolean
  isScrollable: boolean
}

// FormTableActionsColumn props
interface FormTableActionsColumnProps<T extends FormTableRecord = FormTableRecord> {
  record: T
  mode: 'new' | 'existing'
  isEditing: boolean
  isSaving: boolean
  isDeleting: boolean
  canSave?: boolean
  onRemove: (key: T['key']) => void
  onSave: (key: T['key']) => void
  onCancel: () => void
  onResign?: ((key: T['key']) => void) | null
  deleteConfirmTitle?: string
  deleteConfirmDescription?: string
  resignConfirmTitle?: string
  resignConfirmDescription?: string
  useDeleteConfirm?: boolean
  hideDeleteWhenNotEditing?: boolean
  customActionsRenderer?: ((value: any, record: T) => React.ReactNode) | null
}

// ScrollIndicator props
interface ScrollIndicatorProps {
  tableRef: React.RefObject<HTMLDivElement | null>
}

// FormTable props
export interface FormTableProps<T extends FormTableRecord = FormTableRecord> {
  // Data props
  dataSource?: T[]
  columns?: ColumnsType<T>

  // UI props
  title?: string
  addButtonText?: string
  addButtonIcon?: IconDefinition
  addButtonTestId?: string
  emptyText?: string

  // State props
  isAddingNew?: boolean

  // Event handlers
  onAdd?: (() => void) | null
  onRowClick?: ((record: T, event: React.MouseEvent) => void) | null
  onRow?: TableProps<T>['onRow'] | null

  // Actions column props
  includeActions?: boolean
  actionsWidth?: number
  mode?: 'new' | 'existing'
  editingRowKey?: string | number | null
  savingRowKey?: string | number | null
  deletingRowKey?: string | number | null
  // Keyed on the record type's own key so a table of string-keyed rows can hand in string handlers.
  onRemove?: ((key: T['key']) => void) | null
  onResign?: ((key: T['key']) => void) | null
  onSave?: ((key: T['key']) => void) | null
  onCancel?: (() => void) | null
  validateRecord?: ((record: T) => boolean) | null
  deleteConfirmTitle?: string
  deleteConfirmDescription?: string
  resignConfirmTitle?: string
  resignConfirmDescription?: string
  useDeleteConfirm?: boolean
  hideDeleteWhenNotEditing?: boolean
  customActionsRenderer?: ((value: any, record: T) => React.ReactNode) | null

  // Table props
  size?: TableProps<T>['size']
  pagination?: TableProps<T>['pagination']
  showHeader?: boolean
  className?: string
  style?: React.CSSProperties
  loading?: boolean

  // Card props
  cardProps?: Record<string, any>

  // Header actions
  headerActions?: React.ReactNode | null
}

// Actions Column Component
function FormTableActionsColumn<T extends FormTableRecord>({
  record,
  mode,
  isEditing,
  isSaving,
  isDeleting,
  canSave = true,
  onRemove,
  onSave,
  onCancel,
  onResign = null,
  deleteConfirmTitle = 'Delete Team Member',
  deleteConfirmDescription = 'Are you sure you want to remove this team member from the Company Team Table?',
  resignConfirmTitle = 'Terminate Team Member',
  resignConfirmDescription = 'Are you sure you want to terminate this team member?',
  useDeleteConfirm = false,
  hideDeleteWhenNotEditing = false,
  customActionsRenderer = null
}: FormTableActionsColumnProps<T>): React.ReactNode {
  // Always show save/cancel buttons when editing, regardless of mode
  if (isEditing) {
    return (
      <div className='flex items-center gap-1 justify-center'>
        <AntButton
          type='text'
          size={UI.BUTTON_SIZE}
          className={BUTTON_CLASSES.SAVE}
          icon={<FontAwesomeIcon icon={faCheck} className={TEXT_CLASSES.SMALL} />}
          loading={isSaving}
          disabled={!canSave || isSaving}
          onClick={() => onSave(record.key)}
        />
        <AntButton
          type='text'
          size={UI.BUTTON_SIZE}
          className={BUTTON_CLASSES.CANCEL}
          icon={<FontAwesomeIcon icon={faTimes} className={TEXT_CLASSES.SMALL} />}
          disabled={isSaving}
          onClick={onCancel}
        />
      </div>
    )
  }

  // If customActionsRenderer is provided, use it when not editing
  if (customActionsRenderer) {
    return customActionsRenderer(null, record)
  }

  // If hideDeleteWhenNotEditing is true, return null (custom actions column handles delete)
  if (hideDeleteWhenNotEditing) {
    return null
  }

  // For existing mode with resign functionality (CompanyTeamTable), show ellipses menu with resign and delete options
  if (mode === 'existing' && onResign) {
    const menuItems = [
      {
        key: 'resign',
        label: (
          <Popconfirm
            title={resignConfirmTitle}
            description={resignConfirmDescription}
            okText='Terminate'
            cancelText='Cancel'
            okType='danger'
            placement='topRight'
            onConfirm={() => onResign && onResign(record.key)}
            disabled={isDeleting}
          >
            <span className={TEXT_CLASSES.BLUE}>Terminate</span>
          </Popconfirm>
        )
      },
      {
        key: 'delete',
        label: (
          <Popconfirm
            title={deleteConfirmTitle}
            description={deleteConfirmDescription}
            okText='Delete'
            cancelText='Cancel'
            okType='danger'
            placement='topRight'
            onConfirm={() => onRemove(record.key)}
            disabled={isDeleting}
          >
            <span className={TEXT_CLASSES.ERROR}>Delete</span>
          </Popconfirm>
        )
      }
    ]

    return (
      <Dropdown menu={{ items: menuItems }} placement='bottomRight' trigger={['click']} disabled={isDeleting}>
        <Button
          variant='default'
          type='text'
          size={UI.BUTTON_SIZE}
          className={BUTTON_CLASSES.ELLIPSES_BUTTON}
          icon={<FontAwesomeIcon icon={faEllipsisV} className={TEXT_CLASSES.SMALL} />}
          loading={isDeleting}
          disabled={isDeleting}
        />
      </Dropdown>
    )
  }

  // When not editing, show delete button with optional confirmation
  if (useDeleteConfirm) {
    return (
      <Popconfirm
        title={deleteConfirmTitle}
        description={deleteConfirmDescription}
        okText='Delete'
        cancelText='Cancel'
        okType='danger'
        placement='topRight'
        onConfirm={() => onRemove(record.key)}
        okButtonProps={{ loading: isDeleting }}
        cancelButtonProps={{ disabled: isDeleting }}
        disabled={isDeleting}
      >
        <AntButton
          type='text'
          size={UI.BUTTON_SIZE}
          danger
          className={BUTTON_CLASSES.DELETE}
          icon={<FontAwesomeIcon icon={faTrash} className={TEXT_CLASSES.SMALL} />}
          loading={isDeleting}
          disabled={isDeleting}
        />
      </Popconfirm>
    )
  }

  return (
    <AntButton
      type='text'
      size={UI.BUTTON_SIZE}
      danger
      className={BUTTON_CLASSES.DELETE}
      icon={<FontAwesomeIcon icon={faTrash} className={TEXT_CLASSES.SMALL} />}
      loading={isDeleting}
      disabled={isDeleting}
      onClick={() => onRemove(record.key)}
    />
  )
}

// Scroll Indicator Component for Actions Column Header
function ScrollIndicator({ tableRef }: ScrollIndicatorProps): React.ReactElement | null {
  const [scrollState, setScrollState] = useState<ScrollState>({
    canScrollLeft: false,
    canScrollRight: false,
    isScrollable: false
  })

  // Check scroll state
  const checkScrollState = (): void => {
    if (!tableRef.current) return

    const tableContainer = tableRef.current.querySelector(
      '.ant-table-container .ant-table-content'
    ) as HTMLElement | null
    if (!tableContainer) return

    const { scrollLeft, scrollWidth, clientWidth } = tableContainer
    const isScrollable = scrollWidth > clientWidth
    const canScrollLeft = scrollLeft > 0
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1 // -1 for precision

    setScrollState({
      canScrollLeft,
      canScrollRight,
      isScrollable
    })
  }

  // Scroll functions
  const scrollToRight = (): void => {
    if (!tableRef.current) return
    const tableContainer = tableRef.current.querySelector(
      '.ant-table-container .ant-table-content'
    ) as HTMLElement | null
    if (tableContainer) {
      tableContainer.scrollTo({ left: tableContainer.scrollWidth, behavior: 'smooth' })
    }
  }

  const scrollToLeft = (): void => {
    if (!tableRef.current) return
    const tableContainer = tableRef.current.querySelector(
      '.ant-table-container .ant-table-content'
    ) as HTMLElement | null
    if (tableContainer) {
      tableContainer.scrollTo({ left: 0, behavior: 'smooth' })
    }
  }

  // Set up scroll monitoring
  useEffect(() => {
    let tableContainer: HTMLElement | null = null
    let handleScroll: (() => void) | null = null
    let handleResize: (() => void) | null = null

    // Use timeout to ensure DOM is fully rendered
    const initialCheckTimeout = setTimeout(() => {
      if (!tableRef.current) return

      tableContainer = tableRef.current.querySelector('.ant-table-container .ant-table-content') as HTMLElement | null
      if (!tableContainer) return

      // Initial check
      checkScrollState()

      // Monitor scroll events
      handleScroll = () => checkScrollState()
      tableContainer.addEventListener('scroll', handleScroll)

      // Monitor resize events
      handleResize = () => setTimeout(checkScrollState, TIMING.RESIZE_DEBOUNCE)
      window.addEventListener('resize', handleResize)
    }, TIMING.SCROLL_CHECK_DELAY)

    return () => {
      clearTimeout(initialCheckTimeout)
      if (tableContainer && handleScroll) {
        tableContainer.removeEventListener('scroll', handleScroll)
      }
      if (handleResize) {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [])

  // Re-check scroll state when data changes
  useEffect(() => {
    const timeoutId = setTimeout(checkScrollState, TIMING.SCROLL_CHECK_DELAY)
    return () => clearTimeout(timeoutId)
  }, [tableRef])

  // Don't render anything if table is not scrollable
  if (!scrollState.isScrollable) return null

  return (
    <div className='flex justify-center items-center'>
      {scrollState.canScrollLeft ? (
        <Button
          variant='default'
          type='text'
          size={UI.BUTTON_SIZE}
          className={BUTTON_CLASSES.SCROLL_BUTTON}
          icon={<FontAwesomeIcon icon={faChevronLeft} className={TEXT_CLASSES.SMALL} />}
          onClick={scrollToLeft}
          title='Scroll to beginning'
        />
      ) : scrollState.canScrollRight ? (
        <Button
          variant='default'
          type='text'
          size={UI.BUTTON_SIZE}
          className={BUTTON_CLASSES.SCROLL_BUTTON}
          icon={<FontAwesomeIcon icon={faChevronRight} className={TEXT_CLASSES.SMALL} />}
          onClick={scrollToRight}
          title='Scroll to see more'
        />
      ) : null}
    </div>
  )
}

export function FormTable<T extends FormTableRecord = FormTableRecord>({
  // Data props
  dataSource = [],
  columns = [],

  // UI props
  title = 'Form Table',
  addButtonText = 'Add',
  addButtonIcon = faPlus,
  addButtonTestId = DEFAULT_PROPS.addButtonTestId,
  emptyText = 'No data',

  // State props
  isAddingNew = false,

  // Event handlers
  onAdd = null,
  onRowClick = null,
  onRow = null,

  // Actions column props
  includeActions = false,
  actionsWidth = DEFAULT_PROPS.actionsWidth,
  mode = DEFAULT_PROPS.mode,
  editingRowKey = null,
  savingRowKey = null,
  deletingRowKey = null,
  onRemove = null,
  onResign = null,
  onSave = null,
  onCancel = null,
  validateRecord = null,
  deleteConfirmTitle = DEFAULT_PROPS.deleteConfirmTitle,
  deleteConfirmDescription = DEFAULT_PROPS.deleteConfirmDescription,
  resignConfirmTitle = 'Terminate Team Member',
  resignConfirmDescription = 'Are you sure you want to terminate this team member?',
  useDeleteConfirm = false,
  hideDeleteWhenNotEditing = false,
  customActionsRenderer = null,

  // Table props
  size = UI.TABLE_SIZE,
  pagination = false,
  showHeader = true,
  className = '',
  style = {},
  loading = false,

  // Card props
  cardProps = {},

  // Header actions
  headerActions = null
}: FormTableProps<T>): React.ReactElement {
  const tableRef = useRef<HTMLDivElement>(null)

  // Build columns with optional Actions column
  const tableColumns: ColumnsType<T> = [...columns]

  if (includeActions && onRemove) {
    const actionsColumn: ColumnsType<T>[number] = {
      title: () => <ScrollIndicator tableRef={tableRef} />,
      key: 'action',
      width: actionsWidth,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const isEditing = editingRowKey === record.key
        const isSaving = savingRowKey === record.key
        const isDeleting = deletingRowKey === record.key
        const canSave = validateRecord ? validateRecord(record) : true

        return (
          <div className='flex justify-center'>
            <FormTableActionsColumn
              record={record}
              mode={mode}
              isEditing={isEditing}
              isSaving={isSaving}
              isDeleting={isDeleting}
              canSave={canSave}
              onRemove={onRemove}
              onResign={onResign || undefined}
              onSave={onSave || (() => {})}
              onCancel={onCancel || (() => {})}
              deleteConfirmTitle={deleteConfirmTitle}
              deleteConfirmDescription={deleteConfirmDescription}
              resignConfirmTitle={resignConfirmTitle}
              resignConfirmDescription={resignConfirmDescription}
              useDeleteConfirm={useDeleteConfirm}
              hideDeleteWhenNotEditing={hideDeleteWhenNotEditing}
              customActionsRenderer={customActionsRenderer || undefined}
            />
          </div>
        )
      }
    }

    tableColumns.push(actionsColumn)
  }

  // Build header actions - combine add button with any additional headerActions
  const combinedHeaderActions = (
    <div className='flex items-center gap-1 flex-shrink-0'>
      {headerActions}
      {onAdd && (
        <Button
          variant='primary'
          size={UI.BUTTON_SIZE}
          className={`${TEXT_CLASSES.SMALL} px-2 py-1 h-6`}
          icon={<FontAwesomeIcon icon={addButtonIcon} className={TEXT_CLASSES.SMALL} />}
          onClick={onAdd}
          disabled={isAddingNew}
          data-testid={addButtonTestId}
        >
          {addButtonText}
        </Button>
      )}
    </div>
  )

  return (
    <FormSection
      title={title}
      headerActions={headerActions || onAdd ? combinedHeaderActions : null}
      {...cardProps}
    >
      <div ref={tableRef}>
        <Table<T>
          dataSource={dataSource}
          columns={tableColumns}
          pagination={pagination}
          size={size}
          showHeader={showHeader}
          className={`pt-1 pb-1 ${className} h-full`}
          style={{
            fontSize: UI.FONT_SIZE,
            ...style
          }}
          scroll={{ x: 'max-content' }}
          sticky
          loading={loading}
          locale={{
            emptyText: <div className='text-center text-gray-500 text-sm py-2'>{emptyText}</div>
          }}
          onRow={
            onRow ||
            (onRowClick
              ? (record) => ({
                  onClick: (event: React.MouseEvent) => onRowClick(record, event)
                })
              : undefined)
          }
        />
      </div>
    </FormSection>
  )
}
